import { FC, useEffect, useState } from 'react';
import { useRecoilValue, useSetRecoilState } from 'recoil';

import CodeEditor from 'components/common/CodeEditor/CodeEditor';
import PageHeader from 'components/common/PageHeader/PageHeader';
import { ApolloError, useMutation, useQuery } from '@apollo/client';
import { ButtonVariant } from 'components/common/Button/Button';
import { getStringFromError } from 'components/common/CodeEditor/helpers';
import { routeState } from 'components/common/Managers/RouteStateManager.state';
import { pageAlertConfigState } from 'components/common/PageAlert/PageAlert.state';
import { PageAlertVariant } from 'components/common/PageAlert/PageAlert.types';
import { ROUTE } from 'common/constants';
import { HELP_ROUTE } from 'components/help/constants';
import ELEMENT from 'common/element';

import { Prompt } from 'react-router-dom';

import { hasPermissionState } from 'components/account/account.state';
import { Permission } from 'common/graphqlTypes';
import { ADD_PARSER, FORMAT_PARSER, PARSER_DATA } from '../../queries';

type EditParserProps = {
  parser?: string;
  parserValue: string;
  onParserValueChange: (value: string) => void;
  className?: string;
  isTester?: boolean;
};

const EditParser: FC<EditParserProps> = ({
  parser,
  parserValue,
  onParserValueChange,
  className,
  isTester,
}: EditParserProps) => {
  const route = useRecoilValue(routeState);

  const setPageAlertConfig = useSetRecoilState(pageAlertConfigState);
  const showSuccessAlert = (message: string): void => {
    setPageAlertConfig({
      message,
      variant: PageAlertVariant.SUCCESS,
      autoHide: true,
      duration: 1.5,
    });
  };
  const showErrorAlert = (message: string): void => {
    setPageAlertConfig({ message, variant: PageAlertVariant.ERROR, autoHide: true });
  };
  const [inputTouched, setInputTouched] = useState<boolean>(false);
  useQuery(PARSER_DATA, {
    variables: { id: route === ROUTE.LOG_PARSE_TESTER ? '' : parser },
    onCompleted: (result) => {
      onParserValueChange(result?.parser?.content);
    },
  });
  const [addParser] = useMutation(ADD_PARSER, {
    onCompleted: () => {
      setInputTouched(false);
      showSuccessAlert('Parser saved');
    },
    onError: (error: ApolloError) => {
      setInputTouched(false);
      showErrorAlert(getStringFromError(error));
    },
  });
  const [formatParser] = useMutation(FORMAT_PARSER, {
    onCompleted: (data) => {
      if (data?.formatParser) {
        onParserValueChange(data?.formatParser);
        showSuccessAlert('File formatted');
      }
    },
    onError: (error: ApolloError) => {
      showErrorAlert(getStringFromError(error));
    },
  });

  // the editor & data itself are any in the codemirrior-2
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const onCodeChange = (editor: any, _data: any, value: string): void => {
    onParserValueChange(value);
    if (!inputTouched) {
      setInputTouched(true);
    }
  };
  const onSaveParser = (): void => {
    addParser({
      variables: {
        parserName: parser,
        textContent: parserValue,
      },
    });
  };

  const onPrettyPrint = (): void => {
    formatParser({
      variables: {
        id: parser,
        content: parserValue,
      },
    });
  };

  const additionalButtons = [
    {
      name: 'Prettyprint',
      onClick: onPrettyPrint,
      variant: ButtonVariant.SECONDARY,
      disabled: !inputTouched,
      testId: ELEMENT.formatParserButton,
    },
  ];

  if (!isTester) {
    additionalButtons.push({
      name: inputTouched ? 'Save Parser' : 'No Changes',
      onClick: onSaveParser,
      variant: ButtonVariant.PRIMARY,
      disabled: !inputTouched,
      testId: ELEMENT.saveParserButton,
    });
  }

  const handleFileSaveViaShortCut = (): void => {
    if (inputTouched) {
      onSaveParser();
    } else {
      showErrorAlert('There is nothing to save');
    }
  };

  const pageTitle = isTester ? 'Log Parsing Tester' : `Edit Parser: ${parser}`;

  const hasFullAccessPermission = useRecoilValue(hasPermissionState(Permission.fullAccess));
  useEffect(() => {
    if (!hasFullAccessPermission) {
      setPageAlertConfig({
        message: 'You have permissions to edit and test this parser but changes cannot be saved.',
        variant: PageAlertVariant.WARNING,
        autoHide: true,
      });
    }
  }, []);

  return (
    <div className={className}>
      <PageHeader
        title={pageTitle}
        helpLink={HELP_ROUTE.HELP_PARSING_LOGS}
        additionalRightButtons={additionalButtons}
        hasBackButton={!isTester}
      />
      <CodeEditor
        value={parserValue}
        onChange={onCodeChange}
        onSave={!isTester ? handleFileSaveViaShortCut : undefined}
      />
      {!isTester && (
        <Prompt when={inputTouched} message="Are you sure you want to leave? Changes you made may not be saved." />
      )}
    </div>
  );
};

export default EditParser;
